import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
    Col,
    Row,
    UncontrolledTooltip,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    FormFeedback,
    Label,
    Card,
    CardBody,
    Button,
    Pagination,
    PaginationItem,
    PaginationLink,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

// Import services
import {
    fetchProduks,
    createProduk,
    updateProduk,
    deleteProduk,
} from '../../services/produkService';
// Import services untuk master data (contoh)
import { fetchTipeKonten } from '../../services/Master/MasterTipeKonten';
import { fetchJenisSeleksi } from '../../services/Master/MasterJenisSeleksi';

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const Produk = () => {
    // Meta title
    document.title = "Produk";

    // State management
    const navigate = useNavigate();
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [produk, setProduk] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });
    const [inputFilters, setInputFilters] = useState({
        nama_produk: "",
        id_tipe_konten: "",
        id_jenis_seleksi: "",
    });
    const [filters, setFilters] = useState({
        nama_produk: "",
        id_tipe_konten: "",
        id_jenis_seleksi: "",
    });
    const [deleteModal, setDeleteModal] = useState(false);
    
    // State untuk menyimpan data master dari API
    const [tipeKontenOptions, setTipeKontenOptions] = useState([]);
    const [allJenisSeleksiOptions, setAllJenisSeleksiOptions] = useState([]); // Semua jenis seleksi
    const [filteredJenisSeleksiOptions, setFilteredJenisSeleksiOptions] = useState([]); // Jenis seleksi yang difilter
    const [jenisSeleksiKey, setJenisSeleksiKey] = useState(0);
    const [masterDataLoaded, setMasterDataLoaded] = useState(false);

    // Handle delete modal
    const handleCloseDeleteModal = () => {
        setDeleteModal(false);
        setProduk(null);
    };

    const handleConfirmDelete = async () => {
        try {
            if (produk && produk.id_produk) {
                const response = await deleteProduk(produk.id_produk);
                if (response.success) {
                    toast.success("Produk berhasil dihapus");
                    fetchData(pagination.page, pagination.per_page, filters);
                } else {
                    toast.error(response.message || "Gagal menghapus Produk");
                }
            }
        } catch (error) {
            console.error("Error deleting Produk:", error);
            toast.error(error.message || "Gagal menghapus Produk");
        } finally {
            handleCloseDeleteModal();
        }
    };

    // Fetch data
    const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
        try {
            setLoading(true);
            const response = await fetchProduks(page, per_page, filterParams);

            setData(response.data || []);
            setPagination({
                page: response.meta?.page || 1,
                per_page: response.meta?.per_page || 10,
                total: response.meta?.total || 0,
                last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
            });
        } catch (error) {
            console.error("Error in fetchData:", error);
            toast.error("Gagal memuat data Produk");
        } finally {
            setLoading(false);
        }
    };

    // Fetch master data on component mount
    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                setLoading(true);
                const [tipeKontenRes, jenisSeleksiRes] = await Promise.all([
                    fetchTipeKonten(1, 100),
                    fetchJenisSeleksi({ disable_pagination: true })
                ]);

                if (tipeKontenRes.success) {
                    setTipeKontenOptions(tipeKontenRes.data);
                }

                if (jenisSeleksiRes.success) {
                    setAllJenisSeleksiOptions(jenisSeleksiRes.data);
                }

                setMasterDataLoaded(true);
            } catch (error) {
                console.error("Failed to fetch master data:", error);
                toast.error("Gagal memuat data master");
            } finally {
                setLoading(false);
            }
        };

        fetchMasterData();
    }, []);

    // Form validation
    const validation = useFormik({
        initialValues: {
            nama_produk: "",
            id_tipe_konten: "",
            id_jenis_seleksi: "",
            harga: 0,
            harga_coret: 0,
            ribbon: "",
            durasi_hari: 0,
            access_level: "1",
            limit_tryout: 0, // Menggunakan limit_tryout
            // START: Changes
            jenis_paket: "",
            // END: Changes
        },
        validationSchema: Yup.object({
            nama_produk: Yup.string().required("Nama Produk wajib diisi"),
            id_tipe_konten: Yup.number().required("Tipe Konten wajib diisi"),
            id_jenis_seleksi: Yup.number().required("Jenis Seleksi wajib diisi"),
            harga: Yup.number().required("Harga wajib diisi").min(0, "Harga tidak boleh negatif"),
            harga_coret: Yup.number().min(0, "Harga coret tidak boleh negatif"),
            durasi_hari: Yup.number().min(0, "Durasi hari tidak boleh negatif"),
            access_level: Yup.string().required("Level akses wajib diisi"),
            // limit_tryout: Yup.number().required("Batas tryout wajib diisi").min(-1, "Batas tryout tidak boleh kurang dari -1"),
            // START: Changes
            jenis_paket: Yup.string().required("Jenis Paket wajib diisi"),
            // END: Changes
        }),

        onSubmit: async (values) => {
            try {
                const accessLevelNumber = parseInt(values.access_level, 10);
                const idTipeKontenNumber = parseInt(values.id_tipe_konten, 10);
                const idJenisSeleksiNumber = parseInt(values.id_jenis_seleksi, 10);
                const limitTryoutNumber = parseInt(values.limit_tryout, 10); // Menggunakan limit_tryout

                if (isNaN(accessLevelNumber) || isNaN(idTipeKontenNumber) || isNaN(idJenisSeleksiNumber) || isNaN(limitTryoutNumber)) {
                    toast.error("Data yang dikirim tidak valid.");
                    return;
                }
                
                if (accessLevelNumber < 0 || accessLevelNumber > 3) {
                    toast.error("Level akses harus antara 0-3");
                    return;
                }

                const formData = {
                    nama_produk: values.nama_produk,
                    id_tipe_konten: idTipeKontenNumber,
                    id_jenis_seleksi: idJenisSeleksiNumber,
                    harga: values.harga,
                    harga_coret: values.harga_coret,
                    ribbon: values.ribbon,
                    durasi_hari: values.durasi_hari,
                    access_level: accessLevelNumber,
                    limit_tryout: limitTryoutNumber, // Menggunakan limit_tryout
                    // START: Changes
                    jenis_paket: values.jenis_paket,
                    // END: Changes
                };

                let response;
                if (isEdit) {
                    response = await updateProduk(produk.id_produk, formData);
                } else {
                    response = await createProduk(formData);
                }

                if (response.success) {
                    toast.success(response.message || (isEdit ? "Produk berhasil diperbarui" : "Produk berhasil ditambahkan"));
                    setModal(false);
                    fetchData(pagination.page, pagination.per_page, filters);
                } else {
                    if (response.errors) {
                        Object.keys(response.errors).forEach(key => {
                            response.errors[key].forEach(msg => {
                                toast.error(`${key}: ${msg}`);
                            });
                        });
                    } else {
                        toast.error(response.message || "Gagal menyimpan Produk");
                    }
                }
            } catch (error) {
                console.error("Error in form submission:", error);
                toast.error("Terjadi kesalahan saat menyimpan data.");
            }
        },
    });

    // Efek untuk memfilter jenis seleksi ketika tipe konten berubah
    useEffect(() => {
        const selectedTipeKontenId = validation.values.id_tipe_konten;
        if (selectedTipeKontenId) {
            const filteredOptions = allJenisSeleksiOptions.filter(
                (option) => option.id_tipe_konten === parseInt(selectedTipeKontenId, 10)
            );
            setFilteredJenisSeleksiOptions(filteredOptions);
            
            // Reset jenis seleksi jika tidak ada dalam options yang difilter
            if (validation.values.id_jenis_seleksi && 
                !filteredOptions.some(opt => opt.id_jenis_seleksi === parseInt(validation.values.id_jenis_seleksi, 10))) {
                validation.setFieldValue("id_jenis_seleksi", "");
            }
        } else {
            setFilteredJenisSeleksiOptions([]);
            validation.setFieldValue("id_jenis_seleksi", "");
        }
    }, [validation.values.id_tipe_konten, allJenisSeleksiOptions]);

    // Effects
    useEffect(() => {
        if (masterDataLoaded) {
            fetchData(1, pagination.per_page, filters);
        }
    }, [filters, masterDataLoaded]);

    // Handlers
    const handleApplyFilters = () => {
        setFilters(inputFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleResetFilters = () => {
        setInputFilters({
            nama_produk: "",
            id_tipe_konten: "",
            id_jenis_seleksi: "",
        });
        setFilters({
            nama_produk: "",
            id_tipe_konten: "",
            id_jenis_seleksi: "",
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleInputFilterChange = (e) => {
        const { name, value } = e.target;
        setInputFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openAddModal = () => {
        setProduk(null);
        setIsEdit(false);
        validation.resetForm();
        setFilteredJenisSeleksiOptions([]);
        validation.setFieldValue("access_level", "1");
        setJenisSeleksiKey(prevKey => prevKey + 1); // Reset key
        setModal(true);
    };

    const openEditModal = (produkData) => {
        setProduk(produkData);
        setIsEdit(true);
        
        // Filter options berdasarkan tipe konten yang dipilih
        const filteredOptions = allJenisSeleksiOptions.filter(
            (option) => option.id_tipe_konten === parseInt(produkData.id_tipe_konten, 10)
        );
        
        // Set filtered options
        setFilteredJenisSeleksiOptions(filteredOptions);

        const accessLevelValue = produkData.access_level !== null && produkData.access_level !== undefined
            ? produkData.access_level.toString()
            : "1";
        
        // Atur nilai form, ubah id menjadi string untuk kecocokan
        validation.setValues({
            nama_produk: produkData.nama_produk || "",
            id_tipe_konten: produkData.id_tipe_konten ? produkData.id_tipe_konten.toString() : "",
            id_jenis_seleksi: produkData.id_jenis_seleksi ? produkData.id_jenis_seleksi.toString() : "",
            harga: produkData.harga || 0,
            harga_coret: produkData.harga_coret || 0,
            ribbon: produkData.ribbon || "",
            durasi_hari: produkData.durasi_hari || 0,
            access_level: accessLevelValue,
            limit_tryout: produkData.limit_tryout || 0, // Menggunakan limit_tryout
            // START: Changes
            jenis_paket: produkData.jenis_paket || "",
            // END: Changes
        });

        // Force re-render dropdown
        setJenisSeleksiKey(prevKey => prevKey + 1);
        
        // Open modal
        setModal(true);
    };

    const onClickDelete = (produkData) => {
        setProduk(produkData);
        setDeleteModal(true);
    };

    // Columns
    const columns = useMemo(
        () => [
            {
                header: "Nama Produk",
                accessorKey: "nama_produk",
                enableColumnFilter: false,
            },
            {
                header: "Tipe Konten",
                accessorKey: "tipe_konten",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return "-";
                    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
                }
            },
            // START: Changes
            {
                header: "Jenis Paket",
                accessorKey: "jenis_paket",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return "-";
                    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
                }
            },
            // END: Changes
            {
                header: "Jenis Seleksi",
                accessorKey: "jenis_seleksi",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return "-";
                    return value.toUpperCase();
                }
            },
            {
                header: "Harga",
                accessorKey: "harga",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(cellProps.getValue());
                }
            },
            {
                header: "Harga Coret",
                accessorKey: "harga_coret",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    return cellProps.getValue() ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(cellProps.getValue()) : "-";
                }
            },
            {
                header: "Durasi (Hari)",
                accessorKey: "durasi_hari",
                enableColumnFilter: false,
            },
            // {
            //     header: "Batas Tryout",
            //     accessorKey: "limit_tryout", // Menggunakan limit_tryout
            //     enableColumnFilter: false,
            //     cell: ({ getValue }) => {
            //         const value = getValue();
            //         if (value === -1) {
            //             return "Unlimited";
            //         }
            //         return value;
            //     },
            // },
            {
                header: "Ribbon",
                accessorKey: "ribbon",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    return cellProps.getValue() || "-";
                }
            },
            {
                header: "Level Akses",
                accessorKey: "access_level",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    const accessLevel = cellProps.getValue();
                    let levelText = "";

                    const levelValue = typeof accessLevel === 'string' ? parseInt(accessLevel, 10) : accessLevel;

                    switch (levelValue) {
                        case 0:
                            levelText = "Tidak Ada";
                            break;
                        case 1:
                            levelText = "Paket Dasar";
                            break;
                        case 2:
                            levelText = "Premium";
                            break;
                        case 3:
                            levelText = "Ultimate";
                            break;
                        default:
                            levelText = accessLevel || "-";
                    }

                    return levelText;
                }
            },
            {
                header: 'Created At',
                accessorKey: "created_at",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    const rawDate = cellProps.getValue();
                    if (rawDate && rawDate !== "0001-01-01T00:00:00Z") {
                        const date = new Date(rawDate);
                        if (!isNaN(date.getTime())) {
                            return date.toISOString().split('T')[0];
                        }
                    }
                    return "-";
                }
            },
            {
                header: 'Action',
                enableColumnFilter: false,
                cell: (cellProps) => (
                    <div className="hstack gap-2">
                        <Link
                            to={`/detail-produk?id_produk=${cellProps.row.original.id_produk}`}
                            className="btn btn-sm btn-soft-primary"
                            id={`adddetailtooltip-${cellProps.row.original.id_produk}`}
                        >
                            <i className="mdi mdi-plus-box-multiple-outline" />
                            <UncontrolledTooltip placement="top" target={`adddetailtooltip-${cellProps.row.original.id_produk}`}>
                                Detail Produk
                            </UncontrolledTooltip>
                        </Link>
                        <Link
                            to={`/produk-konten?id_produk=${cellProps.row.original.id_produk}`}
                            className="btn btn-sm btn-soft-info"
                            id={`addkontentooltip-${cellProps.row.original.id_produk}`}
                        >
                            <i className="mdi mdi-text-box-plus-outline" />
                            <UncontrolledTooltip placement="top" target={`addkontentooltip-${cellProps.row.original.id_produk}`}>
                                Produk Konten
                            </UncontrolledTooltip>
                        </Link>
                        <Link
                            to="#"
                            className="btn btn-sm btn-soft-warning"
                            onClick={() => openEditModal(cellProps.row.original)}
                            id={`edittooltip-${cellProps.row.original.id_produk}`}
                        >
                            <i className="mdi mdi-pencil-outline" />
                            <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_produk}`}>
                                Edit
                            </UncontrolledTooltip>
                        </Link>
                        <Link
                            to="#"
                            className="btn btn-sm btn-soft-danger"
                            onClick={() => onClickDelete(cellProps.row.original)}
                            id={`deletetooltip-${cellProps.row.original.id_produk}`}
                        >
                            <i className="mdi mdi-delete-outline" />
                            <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_produk}`}>
                                Delete
                            </UncontrolledTooltip>
                        </Link>
                    </div>
                )
            }
        ],
        []
    );

    // Custom Pagination
    const CustomPagination = () => {
        const { page, last_page, total, per_page } = pagination;

        const getPageNumbers = () => {
            const pages = [];
            const maxVisiblePages = 5;
            let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

            if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(
                    <PaginationItem key={i} active={i === page}>
                        <PaginationLink onClick={() => fetchData(i, per_page, filters)}>
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            }

            return pages;
        };

        return (
            <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted">
                    Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
                    <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari total{' '}
                    <span className="fw-semibold">{total}</span> entri
                </div>
                <div className="d-flex align-items-center">
                    <Pagination className="pagination pagination-rounded mb-0">
                        <PaginationItem disabled={page === 1}>
                            <PaginationLink
                                previous
                                onClick={() => page > 1 && fetchData(page - 1, per_page, filters)}
                            >
                                &lt;
                            </PaginationLink>
                        </PaginationItem>
                        {getPageNumbers()}
                        <PaginationItem disabled={page === last_page}>
                            <PaginationLink
                                next
                                onClick={() => page < last_page && fetchData(page + 1, per_page, filters)}
                            >
                                &gt;
                            </PaginationLink>
                        </PaginationItem>
                    </Pagination>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleConfirmDelete}
                onCloseClick={handleCloseDeleteModal}
            />
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Master Data" breadcrumbItem="Master Produk" />
                    {
                        loading ? <Spinners setLoading={setLoading} />
                            :
                            <Row>
                                <Col lg="12">
                                    <Card>
                                        <CardBody className="border-bottom">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                                                <h5 className="mb-2 mb-md-0 card-title flex-grow-1">List Produk</h5>
                                                {/* Filter Inputs */}
                                               
                                                {/* Action Buttons */}
                                                <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                                                    <Button color="primary" onClick={openAddModal}>
                                                        <i className="mdi mdi-plus me-1"></i>
                                                        Tambah Produk
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                        <CardBody>
                                            <TableContainer
                                                columns={columns}
                                                data={data}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                customPageSize={pagination.per_page}
                                                tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                                            />
                                            <CustomPagination />
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                    }

                    {/* Modal for Add/Edit Produk */}
                    <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
                        <ModalHeader toggle={() => setModal(false)} tag="h4">
                            {!!isEdit ? "Edit Produk" : "Tambah Produk"}
                        </ModalHeader>
                        <ModalBody>
                            <Form onSubmit={validation.handleSubmit}>
                                <Row>
                                    <Col md="12">
                                        <div className="mb-3">
                                            <Label>Nama Produk</Label>
                                            <Input
                                                name="nama_produk"
                                                type="text"
                                                placeholder="Nama Produk"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.nama_produk || ""}
                                                invalid={!!(validation.touched.nama_produk && validation.errors.nama_produk)}
                                            />
                                            <FormFeedback>{validation.errors.nama_produk}</FormFeedback>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="id_tipe_konten">Tipe Konten</Label>
                                            <Input
                                                name="id_tipe_konten"
                                                type="select"
                                                className="form-select"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.id_tipe_konten || ""}
                                                invalid={!!(validation.touched.id_tipe_konten && validation.errors.id_tipe_konten)}
                                            >
                                                <option value="">Pilih Tipe Konten</option>
                                                {tipeKontenOptions.map((option) => (
                                                    <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                                                        {option.tipe_konten}
                                                    </option>
                                                ))}
                                            </Input>
                                            <FormFeedback>{validation.errors.id_tipe_konten}</FormFeedback>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="id_jenis_seleksi">Jenis Seleksi</Label>
                                            <Input
                                                key={jenisSeleksiKey}
                                                name="id_jenis_seleksi"
                                                type="select"
                                                className="form-select"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.id_jenis_seleksi || ""}
                                                invalid={!!(validation.touched.id_jenis_seleksi && validation.errors.id_jenis_seleksi)}
                                                disabled={!validation.values.id_tipe_konten}
                                            >
                                                <option value="">Pilih Jenis Seleksi</option>
                                                {filteredJenisSeleksiOptions.map((option) => (
                                                    <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                                                        {option.nama_seleksi}
                                                    </option>
                                                ))}
                                            </Input>
                                            <FormFeedback>{validation.errors.id_jenis_seleksi}</FormFeedback>
                                        </div>
                                    </Col>
                                </Row>
                                {/* START: Changes */}
                     
                                {/* END: Changes */}
                                <Row>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="harga">Harga</Label>
                                            <Input
                                                name="harga"
                                                type="number"
                                                placeholder="Harga"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.harga || ""}
                                                invalid={!!(validation.touched.harga && validation.errors.harga)}
                                            />
                                            <FormFeedback>{validation.errors.harga}</FormFeedback>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="harga_coret">Harga Coret</Label>
                                            <Input
                                                name="harga_coret"
                                                type="number"
                                                placeholder="Harga Coret (Opsional)"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.harga_coret || ""}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row>

                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="ribbon">Ribbon</Label>
                                            <Input
                                                name="ribbon"
                                                type="text"
                                                placeholder="Ribbon"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.ribbon || ""}
                                            />
                                        </div>
                                    </Col>
                                                                        <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="access_level">Level Akses</Label>
                                            <Input
                                                name="access_level"
                                                type="select"
                                                className="form-select"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.access_level || ""}
                                                invalid={!!(validation.touched.access_level && validation.errors.access_level)}
                                            >
                                                <option value="1">Paket Dasar</option>
                                                <option value="2">Premium</option>
                                                <option value="3">Ultimate</option>
                                            </Input>
                                            <FormFeedback>{validation.errors.access_level}</FormFeedback>
                                        </div>
                                    </Col>
                                </Row>
           
                                <Row>

                                     <Col md="6">
                                        <div className="mb-3">
                                            <Label htmlFor="jenis_paket">Jenis Paket</Label>
                                            <Input
                                                name="jenis_paket"
                                                type="select"
                                                className="form-select"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.jenis_paket || ""}
                                                invalid={!!(validation.touched.jenis_paket && validation.errors.jenis_paket)}
                                            >
                                                <option value="">Pilih Jenis Paket</option>
                                                <option value="tryout">Tryout</option>
                                                <option value="bimbel">Bimbel</option>
                                            </Input>
                                            <FormFeedback>{validation.errors.jenis_paket}</FormFeedback>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div className="text-end mt-4">
                                    <Button type="submit" color="primary" className="btn-label">
                                        Simpan
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>
                </div>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default Produk;